import { useMemo } from "react";

import useCommonAddress from "@repo/shopifyV2/hooks/useAddress";
import type { ICountryData } from "@repo/shopifyV2/types/address";
import type { IAddressFormData, TIntlAddressForm } from "@repo/shopifyV2/types/address";
import type { IAddressOption } from "@repo/shopifyV2/types/hooks";
import type { InputMaybe, MailingAddress } from "@repo/shopifyV2/types/storefront.types";

import addressForms from "@assets/addressForms.json";

interface IProps {
  countryCode?: InputMaybe<string> | undefined;
}

export default function useAddress({ countryCode = "US" }: IProps) {
  const addressFormKeys: (keyof MailingAddress)[] = ["firstName", "lastName", "province", "address1", "phone"];
  const addressKeys: (keyof MailingAddress)[] = ["firstName", "lastName", "address1", "phone"];
  const addressFormData: TIntlAddressForm = addressForms as TIntlAddressForm;

  const countryName = useMemo(() => {
    const findCountryInfo = Object.entries(addressFormData).find(([key]) => key === countryCode);
    const findCountryName = findCountryInfo?.find((item) => typeof item === "object" && item !== null && "name" in item);

    return findCountryName?.name || "United States";
  }, [countryCode, addressFormData]);

  const countryOptions: IAddressOption[] = useMemo(
    () => [
      { name: "Select Country", label: "Select Country", value: "-1" },
      ...Object.keys(addressFormData).map((key) => ({
        name: addressFormData[key].name,
        label: addressFormData[key].name,
        value: key,
      })),
    ],
    [addressFormData],
  );

  const stateOptions: IAddressOption[] = useMemo(() => {
    const selectedCountryCode = countryCode || "US";
    const states = addressFormData[selectedCountryCode]?.states || {};
    if (Object.keys(states).length === 0) return [{ name: "Select State", label: "Select State", value: "-1" }];

    return [
      { name: "Select State", label: "Select State", value: "-1" },
      ...Object.entries(states).map(([name, code]) => ({ name, label: name, value: code })),
    ];
  }, [addressFormData, countryCode]);

  const formOrder: IAddressFormData[][] = useMemo(
    () => addressFormData[countryCode || "US"]?.form_order || [],
    [countryCode, addressFormData],
  );

  return {
    addressFormKeys,
    addressKeys,
    addressFormData,
    countryOptions,
    countryName,
    stateOptions,
    formOrder,
  };
}
